/**
 * Typing notes into a score: the caret, what the next note will be, and the edits that
 * follow from a keystroke.
 *
 * This is a pure function of (score, state, input). It holds no score of its own and makes
 * no edit; it answers with the commands the caller should apply and the state the caller
 * should keep. That is what lets one state machine sit behind a computer keyboard, a MIDI
 * keyboard or a mouse, and it is what makes every step of note entry undoable by the
 * ordinary means rather than by a mode that remembers what it did.
 *
 * This is the upper half: what a keystroke means where the caret stands. The caret itself,
 * and the writing of one event at it, are in {@link ./entry-write}; the note that a letter
 * or a played key turns out to be, and the writing of it, are in {@link ./entry-note}; the
 * fretted staves are in {@link ./entry-fret}, because a digit there is a note rather than a
 * note value.
 *
 * A key played on a MIDI keyboard arrives here as an input like any other, and the pitch it
 * means is worked out in {@link ./entry-midi}. Everything after that -- the caret stepping
 * on, the tie, the grace mode, the fret a note is taken at -- is what a typed letter gets,
 * because by then there is no difference between them.
 *
 * Entry writes over what is there rather than pushing it along; `entry-voice` says why. The
 * one exception is the grace note, which takes no time from the bar and is therefore slipped
 * in front of the note it leans on: {@link ./entry-grace}.
 */

import { rest as makeRest } from "../document/create";
import type { NoteType, TupletRatio } from "../document/duration";
import { plain, ticksOf, withDots, withTuplet } from "../document/duration";
import { locateEvent } from "../document/query";
import type { NoteEvent, Score } from "../document/types";
import { asTyped, staffAt } from "./entry-spell";
import type { Typed } from "./entry-spell";
import type { Caret } from "./entry-place";
import { indexAt, placeOf, rewrite, stepped } from "./entry-place";
import { refuse, write } from "./entry-write";
import type { EntryResult, EntryState } from "./entry-write";
import { chordSound, typedSound, writeSound } from "./entry-note";
import { playedSound } from "./entry-midi";
import { moveString, writeFret } from "./entry-fret";
import { repitch, targetEvent } from "./entry-pitch";
import type { PitchMove } from "./entry-pitch";
import { erase } from "./entry-voice";
import { lastGraceAt } from "./entry-grace";

export type { Caret } from "./entry-place";
export { IDLE } from "./entry-write";
export type { EntryResult, EntryState } from "./entry-write";
export type { Sounded } from "./entry-note";
export type { PitchMove } from "./entry-pitch";

/** One thing the user did. */
export type EntryInput =
  | { readonly kind: "place"; readonly caret: Caret }
  | { readonly kind: "duration"; readonly type: NoteType }
  | { readonly kind: "dots"; readonly dots: number }
  | { readonly kind: "tuplet"; readonly ratio: TupletRatio | null }
  | ({ readonly kind: "note" } & Typed)
  | ({ readonly kind: "chord" } & Typed)
  | { readonly kind: "rest" }
  /**
   * A key played on a MIDI keyboard, given as the MIDI number it sounds.
   *
   * `chord` is what a second key held down with the first means: the note joins the one
   * just written instead of following it. Which key was struck first is the keyboard's
   * business, not this machine's -- see `midi/keyboard.ts`.
   */
  | { readonly kind: "played"; readonly note: number; readonly chord: boolean }
  /** A fret typed on a tab staff, which writes the note that string stops there. */
  | { readonly kind: "fret"; readonly fret: number }
  /** Move to another string, which is how a tab staff's up and down arrows read. */
  | { readonly kind: "string"; readonly delta: number }
  | { readonly kind: "delete" }
  | { readonly kind: "move"; readonly delta: number }
  /** Move the note last written, or the one under the caret: see {@link PitchMove}. */
  | ({ readonly kind: "pitch" } & PitchMove)
  | { readonly kind: "tie" }
  /** Write the notes that follow as graces of this kind, or stop: see {@link EntryState.grace}. */
  | { readonly kind: "grace"; readonly grace: NoteEvent["grace"] }
  /** Whether the letters being typed name concert pitches: see {@link EntryState.concert}. */
  | { readonly kind: "concert"; readonly on: boolean };

/** A key played: settle what it means on this staff, then write it or add it to the chord. */
function playNote(score: Score, state: EntryState, note: number, chord: boolean): EntryResult {
  const sound = playedSound(score, state, note, !chord && state.tie);
  if (typeof sound === "string") return refuse(state, sound);
  return chord ? chordSound(score, state, sound) : writeSound(score, state, sound);
}

/** Silence what the caret stands on, leaving the bar as long as it was. */
function deleteAt(score: Score, state: EntryState): EntryResult {
  const caret = state.caret;
  if (caret === null) return refuse(state, "there is nothing to delete: click a note first");
  const place = placeOf(score, caret);
  if (typeof place === "string") return refuse(state, place);

  // In grace mode the thing to delete is the grace just typed, not the note it leans on:
  // that is what makes a mistyped run of graces correctable without leaving the mode.
  const grace = state.grace === null ? -1 : lastGraceAt(place.voice, caret.onset, place.capacity);
  const at = grace < 0 ? indexAt(place.voice, caret.onset, place.capacity) : grace;
  if (at < 0) return refuse(state, "there is nothing to delete there");
  const erased = erase(place.voice, at, place.capacity);
  if (typeof erased === "string") return refuse(state, erased);

  return {
    state: { ...state, last: null, tie: false },
    commands: [rewrite(caret, place, erased.events)],
    refusal: null,
  };
}

/** Move a note already written, leaving the caret where it stands. */
function movePitch(score: Score, state: EntryState, move: PitchMove): EntryResult {
  const target = targetEvent(score, state.caret, state.last);
  if (target === null) return refuse(state, "there is no note to move: click one first");
  const moved = repitch(score, target, move);
  if (typeof moved === "string") return refuse(state, moved);

  // The register a bare letter is next spelled in is kept in the letters the user types,
  // which in a concert score are not the letters the note is stored under.
  const first = moved.pitches[0];
  const at = locateEvent(score, target);
  const shown =
    first === undefined
      ? null
      : asTyped(first, staffAt(score, at?.partId ?? null, at?.measureId ?? null, state.concert));
  const spelled = shown === null ? state : { ...state, step: shown.step, octave: shown.octave };
  return { state: { ...spelled, last: target }, commands: [moved.command], refusal: null };
}

/** Step the caret one event either way, writing nothing. */
function moveCaret(score: Score, state: EntryState, delta: number): EntryResult {
  const caret = state.caret;
  if (caret === null) return refuse(state, "there is no caret to move");
  const moved = stepped(score, caret, delta);
  if (typeof moved === "string") return refuse(state, moved);
  return { state: { ...state, caret: moved, last: null }, commands: [], refusal: null };
}

/**
 * What follows from one input. Nothing here changes the score; the caller applies the edits.
 *
 * Every input but another fret ends a run of fret digits, which is what keeps `1` `2` the
 * twelfth fret while `1`, an arrow, `2` is two notes.
 */
export function respond(score: Score, state: EntryState, input: EntryInput): EntryResult {
  const result = decide(score, state, input);
  if (input.kind === "fret") return result;
  return result.state.fretting === null
    ? result
    : { ...result, state: { ...result.state, fretting: null } };
}

function decide(score: Score, state: EntryState, input: EntryInput): EntryResult {
  const quiet = (next: EntryState): EntryResult => ({ state: next, commands: [], refusal: null });

  switch (input.kind) {
    case "place":
      return quiet({ ...state, caret: input.caret, last: null, tie: false });
    case "duration":
      return quiet({ ...state, duration: plain(input.type) });
    case "dots":
      return quiet({ ...state, duration: withDots(state.duration, input.dots) });
    case "tuplet":
      return quiet({ ...state, duration: withTuplet(state.duration, input.ratio) });
    case "note":
      return writeSound(score, state, typedSound(score, state, input, state.tie));
    case "chord":
      return chordSound(score, state, typedSound(score, state, input, false));
    case "played":
      return playNote(score, state, input.note, input.chord);
    case "rest":
      return write(score, state, makeRest(state.duration));
    case "fret":
      return writeFret(score, state, input.fret);
    case "string":
      return moveString(score, state, input.delta);
    case "delete":
      return deleteAt(score, state);
    case "move":
      return moveCaret(score, state, input.delta);
    case "pitch":
      return movePitch(score, state, input);
    case "tie":
      return state.last === null
        ? refuse(state, "there is no note to tie from")
        : quiet({ ...state, tie: !state.tie });
    // The run is finished, so the next chord note or tie has nothing to attach to: a grace
    // and the note it leans on are not one event.
    case "grace":
      return quiet({ ...state, grace: input.grace, last: null, tie: false });
    // The letter last typed was named in the other reading, so it is forgotten rather than
    // used to choose the octave the next bare letter lands in.
    case "concert":
      return quiet({ ...state, concert: input.on, step: null });
  }
}

/** Ticks the state's current duration lasts, for a caller drawing the caret. */
export function caretLength(state: EntryState): number {
  return ticksOf(state.duration);
}
